import { ContactKind, ManagerModel } from '@ail/customers-api'
import { DatabaseReportAdminViewModel } from '@ail/reports-api'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { printDate } from '../../../../helpers/Utils'
import { isDatabaseReportAdmin, useGetReportsAdminQuery, useSendReportMutation } from '../../../../redux/api/reportsApi'
import { ICustomerViewModel } from '../../../../types/interfaces'
import { Modal } from '../../../common/Modals/Modal'
import './SendReports.scss'

interface IProps {
  company?: ICustomerViewModel
  close: () => void
  onReportSent?: () => void
}

export default function SendReports(props: IProps) {
  const [report, setReport] = useState<DatabaseReportAdminViewModel>()
  const [language, setLanguage] = useState<string>('en')
  const [recipientSearch, setRecipientSearch] = useState<string>('')
  const [selectedRecipients, setSelectedRecipients] = useState<ManagerModel[]>([])
  const { t } = useTranslation('common')

  const reportDefs = useGetReportsAdminQuery()
  const [sendReport] = useSendReportMutation()

  const standardReports = useMemo(() => {
    return reportDefs.currentData?.filter(isDatabaseReportAdmin).filter((r) => r.isStandardReport) ?? []
  }, [reportDefs.currentData])

  useEffect(() => {
    setSelectedRecipients([])
    setReport(undefined)
  }, [props.company])

  const recipients = useMemo(() => {
    if (!props.company) {
      return []
    }
    return props.company.managers.filter((m) => {
      return (
        m.displayName?.toLowerCase().includes(recipientSearch.toLowerCase()) &&
        m.contacts.some((c) => c.kind === ContactKind.Email) &&
        !selectedRecipients.includes(m)
      )
    })
  }, [props.company, recipientSearch, selectedRecipients])

  const handleSubmit = () => {
    if (!report) return
    const recipients = selectedRecipients.map((r) => r.contacts.find((c) => c.kind === ContactKind.Email)?.value)
    const parameters = report.parameters.reduce((result, p) => {
      if (p.securityType === 'Customer') {
        result[p.name] = props.company?.externalReference?.split(':')[1]
      } else if (p.securityType === 'RootCustomer') {
        const rootParent = props.company?.parents.find((p) => !p.parents || !p.parents.length)
        const customer = rootParent ?? props.company
        result[p.name] = customer?.externalReference?.split(':')[1]
      } else if (!p.securityType) {
        result[p.name] = p.value
      }
      return result
    }, {} as Partial<Record<string, string>>)

    const parametersValid = report.parameters.every((p) => !p.securityType || parameters?.[p.name] != null)

    if (parametersValid) {
      sendReport({
        reportId: report.id,
        model: {
          parameters: parameters as Record<string, string>,
          recipients: recipients as string[],
          language,
        },
      })
        .unwrap()
        .then(() => {
          props.close()
          alert('Report has been sent!')
        })
    } else {
      alert('Invalid report parameters')
    }
  }

  return (
    <Modal onClose={() => props.close()} visible={!!props.company}>
      <div className="send-reports">
        {/* Title */}
        <div className="title">
          <i className="icon-report-send" />
          <div className="reportTitle">
            {props.company?.name}
            <span>{t('SEND_REPORT')}</span>
          </div>
        </div>

        {/* Available reports */}
        <div className="section flex grid-col-4">
          <div className="available-reports">
            <span className="section-name">{t('AVAILABLE_REPORTS')}</span>
            <table>
              <tr>
                <th>{t('NAME')}</th>
                <th>{t('DATE')}</th>
                <th></th>
              </tr>
              {standardReports.map((r, i) => (
                <tr key={i} onClick={() => setReport(r)}>
                  <td>
                    <div className="reportName">
                      <i className="icon-excel" />
                      {r.name}
                    </div>
                  </td>
                  <td>{printDate(r.created)}</td>
                  <td>
                    <label className="control control-radio">
                      <input type="radio" readOnly={true} name="report-name" checked={r === report} />
                      <div className="control_indicator"></div>
                    </label>
                  </td>
                </tr>
              ))}
            </table>
          </div>

          {/* Select recipients */}
          <div className="select-recipients">
            <span className="section-name">{t('SELECT_RECIPIENTS')}</span>
            <div className="dashed-box">
              <div className="input-area">
                <input
                  className="transparent"
                  placeholder={t('SEARCH_RECIPIENTS')}
                  value={recipientSearch}
                  onChange={(e) => setRecipientSearch(e.target.value)}
                />
                {!!recipientSearch && <i className="icon-clear" onClick={() => setRecipientSearch('')}></i>}
                <i className="icon-search"></i>
              </div>
              <div className="recipient-list">
                {recipients.map((r, i) => (
                  <div key={i} className="recipient">
                    <span className="name">{r.displayName}</span>
                    <span className="plus" onClick={() => setSelectedRecipients([...selectedRecipients, r])}>
                      <i className="icon-add"></i>
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {(selectedRecipients.length > 0 || report) && <div className="line"></div>}

        <div className="section grid-col-4">
          <div className="resultArea">
            {selectedRecipients.length > 0 && (
              <div className="selected-recipients">
                <span className="section-name">{t('RECIPIENTS')}:</span>
                <div className="recipient-list">
                  {selectedRecipients.map((r, i) => (
                    <div key={i} className="recipient">
                      <span className="name">{r.displayName}</span>
                      <span
                        className="minus"
                        onClick={() => setSelectedRecipients((curr) => curr.filter((cr) => cr !== r))}
                      >
                        <i className="icon-clear"></i>
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {recipients.length > 0 && report && <div className="line"></div>}

            {/* Selected report */}
            {report && (
              <div className="selected-report">
                <span className="section-name">{t('REPORT')}</span>
                <div className="report-list">
                  <div className="report">
                    <span className="name">
                      <i className="icon-excel"></i> {report.name}
                    </span>
                    <span className="minus" onClick={() => setReport(undefined)}>
                      <i className="icon-clear"></i>
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="line"></div>

          {/* Select language */}
          <div className="section grid-col-4">
            <span className="section-name">{t('SELECT_LANGUAGE')}</span>
            <div className="flex">
              <div>
                <div className="select-language">
                  <label className="control control-radio">
                    {t('ENGLISH')}
                    <input
                      type="radio"
                      name="language"
                      value="en"
                      checked={language == 'en'}
                      onChange={() => setLanguage('en')}
                    />
                    <div className="control_indicator"></div>
                  </label>
                  <label className="control control-radio">
                    {t('GERMAN')}
                    <input
                      type="radio"
                      name="language"
                      value="de"
                      checked={language == 'de'}
                      onChange={() => setLanguage('de')}
                    />
                    <div className="control_indicator"></div>
                  </label>
                  <label className="control control-radio">
                    {t('FRENCH')}
                    <input
                      type="radio"
                      name="language"
                      value="fr"
                      checked={language == 'fr'}
                      onChange={() => setLanguage('fr')}
                    />
                    <div className="control_indicator"></div>
                  </label>
                  <label className="control control-radio">
                    {t('ITALIAN')}
                    <input
                      type="radio"
                      name="language"
                      value="it"
                      checked={language == 'it'}
                      onChange={() => setLanguage('it')}
                    />
                    <div className="control_indicator"></div>
                  </label>
                </div>
              </div>
              <div>
                {selectedRecipients.length > 0 && report && language && (
                  <button className="btn reverse" onClick={handleSubmit}>
                    <i className="icon-report-send"></i> {t('SUBMIT')}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  )
}
